import { connect } from 'react-redux';
import { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import EmojiPicker from 'emoji-picker-react';

import { hasCompletedOnboarding, getOnboardingRoute } from '../../utils/AuthorizationCheck';

const LeaguePersonalizationInput = ({auth}) => {        
    const history = useHistory();
    const [leagueContext, setLeagueContext] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [teamNames, setTeamNames] = useState([]);
    const [isLoadingTeamNames, setIsLoadingTeamNames] = useState(true);
    const [isInitializing, setIsInitializing] = useState(true);
    const [showExamples, setShowExamples] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const maxCharacters = 10000;

    const loadExistingLeagueContext = useCallback(async () => {
        try {
            // Get platform_league_id from database
            if (auth?.user?.id) {
                const response = await axios.get('/api/get/user-league-data');
                if (response.data.success) {
                    const platform_league_id = response.data.platform_league_id;
                    
                    // Fetch league context from database
                    const contextResponse = await axios.get(`/api/get/league-context/${platform_league_id}`);
                    if (contextResponse.data.success && contextResponse.data.league_context) {
                        setLeagueContext(contextResponse.data.league_context);
                        return;
                    }
                }
            }
            
            // If no data found, textarea stays empty with placeholder
            
        } catch (error) {
            console.error('Error loading existing league context:', error);
        }
    }, [auth?.user?.id]);

    const fetchTeamNames = useCallback(async () => {
        try {
            setIsLoadingTeamNames(true);
            
            // Get league data from database
            if (auth?.user?.id) {
                const response = await axios.get('/api/get/user-league-data');
                if (response.data.success) {
                    const platform_league_id = response.data.platform_league_id;
                    
                    const teamResponse = await axios.get(`/api/get/league-team-names/${platform_league_id}`);
                    
                    if (teamResponse.data.success && teamResponse.data.team_names.length > 0) {
                        setTeamNames(teamResponse.data.team_names);
                    } else {
                        setTeamNames([]);
                    }
                } else {
                    setTeamNames([]);
                }
            } else {
                setTeamNames([]);
            }
        } catch (error) {
            console.error('Error fetching team names:', error);
            setTeamNames([]);
        } finally {
            setIsLoadingTeamNames(false);
        }
    }, [auth?.user?.id]);

    useEffect(() => {                
        // Check if user is authenticated and has completed onboarding
        if (auth?.user?.id && hasCompletedOnboarding(auth)) {
            history.push('/dashboard');
            return;
        }
        
        // Check if user should be on a different onboarding step
        if (auth?.user?.id && !hasCompletedOnboarding(auth)) {
            const correctRoute = getOnboardingRoute(auth);
            if (correctRoute !== '/onboarding/league-context') {
                history.push(correctRoute);
                return;
            }
        }
        
        // No need to check previous step - database step progression is handled by backend APIs
        
        // Load existing league context data (database first, localStorage fallback)
        loadExistingLeagueContext();

        // Fetch team names for examples
        fetchTeamNames();
        
        // Mark initialization as complete after all checks
        setIsInitializing(false);
    }, [auth, history, loadExistingLeagueContext, fetchTeamNames]);

    // Close emoji picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showEmojiPicker && !event.target.closest('.emoji-picker-container')) {
                setShowEmojiPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker]);

    const getExampleText = () => {
        if (isLoadingTeamNames) {
            return "Loading examples...";
        }

        if (teamNames.length >= 2) {
            // Use real team names
            const team1 = teamNames[0];
            const team2 = teamNames[1];
            const team3 = teamNames.length > 2 ? teamNames[2] : teamNames[0];
            const team4 = teamNames.length > 3 ? teamNames[3] : teamNames[1];
            
            return `**${team1}** is run by John and he's brothers with **${team2}**. ${team1} talks a lot of crap and he's a software engineer who's always trying to make shady trades.

• **${team3}** never sets their lineup and somehow still wins
• **${team4}** is the league taco but thinks they're a fantasy guru
• The league chat gets heated during Monday Night Football 🏈`;
        } else {
            // Use generic examples
            return `**[Team 1 Name]** is run by John and he's brothers with **[Team 2 Name]**. [Team 1 Name] talks a lot of crap and he's a software engineer who's always trying to make shady trades.

• **[Team 3 Name]** never sets their lineup and somehow still wins
• **[Team 4 Name]** is the league taco but thinks they're a fantasy guru  
• The league chat gets heated during Monday Night Football 🏈`;
        }
    };

    const handleTextChange = (e) => {
        const newText = e.target.value;
        if (newText.length <= maxCharacters) {
            setLeagueContext(newText);
        }
    };

    const formatText = (type) => {
        const textarea = document.getElementById('leagueContextTextarea');
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = leagueContext.substring(start, end);
        
        if (selectedText) {
            let formattedText = '';
            if (type === 'bold') {
                formattedText = `**${selectedText}**`;
            } else if (type === 'bullet') {
                formattedText = `• ${selectedText}`;
            }
            
            const newText = leagueContext.substring(0, start) + formattedText + leagueContext.substring(end);
            if (newText.length <= maxCharacters) {
                setLeagueContext(newText);
                
                // Restore cursor position
                setTimeout(() => {
                    textarea.focus();
                    textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
                }, 0);
            }
        }
    };

    const onEmojiClick = (emojiData) => {
        const textarea = document.getElementById('leagueContextTextarea');
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        
        const newText = leagueContext.substring(0, start) + emojiData.emoji + leagueContext.substring(end);
        if (newText.length <= maxCharacters) {
            setLeagueContext(newText);
            
            // Restore cursor position
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + emojiData.emoji.length, start + emojiData.emoji.length);
            }, 0);
        }
        
        // Close emoji picker after selection
        setShowEmojiPicker(false);
    };

    const handleSkip = async () => {
        setIsSubmitting(true);
        
        try {
            // Save empty league context to backend if authenticated
            if (auth?.user?.id) {
                // Get league data from database
                const response = await axios.get('/api/get/user-league-data');
                if (response.data.success) {
                    const platform_league_id = response.data.platform_league_id;
                    
                    await axios.post('/api/post/league-context', {
                        league_context: '',
                        platform_league_id: platform_league_id
                    });
                    
                    // Update onboarding step to 7 after successful league context save
                    await axios.post('/api/post/onboarding-step/7');
                }
            }
            
            // Move to next step
            history.push('/onboarding/weekly-context');
        } catch (error) {
            console.error('Error skipping league context:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleContinue = async () => {
        setIsSubmitting(true);
        
        try {
            // Save league context to backend if authenticated
            if (auth?.user?.id) {
                // Get league data from database
                const response = await axios.get('/api/get/user-league-data');
                if (!response.data.success) {
                    throw new Error('League information not found. Please restart onboarding from league selection.');
                }
                
                const platform_league_id = response.data.platform_league_id;
                
                await axios.post('/api/post/league-context', {
                    league_context: leagueContext.trim(),
                    platform_league_id: platform_league_id
                });
                
                // Update onboarding step to 7 after successful league context save
                await axios.post('/api/post/onboarding-step/7');
            }
            
            // Move to next step
            history.push('/onboarding/weekly-context');
        } catch (error) {
            console.error('Error saving league context:', error);
            // Show user-friendly error message
            alert('Failed to save league context. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getProgressPercentage = () => {
        // League context is step 6 of 8, so (6-1)/8 = 62.5%
        return 62.5;
    };

    const getRemainingCharacters = () => {
        return maxCharacters - leagueContext.length;
    };

    // Show loading state while initializing
    if (isInitializing) {
        return (
            <div className="pt-5 onboarding-section">    
                <Row className="onboarding-header">
                    <Col xs={1} md={2}></Col>
                    <Col xs={10} md={8}>       
                            
                        <Row className="text-center onboarding-title-row">
                            <Col className="onboarding-title">Loading League Context</Col>
                        </Row>
                        
                        {/* Progress Bar */}
                        <Row className="mt-4">
                            <Col>
                                <div className="text-center mb-1">
                                    <span className="onboarding-progress-percentage">
                                        {Math.round(getProgressPercentage())}% Complete
                                    </span>
                                </div>
                                <div className="onboarding-progress">
                                    <div 
                                        className="onboarding-progress-bar"
                                        style={{ width: `${getProgressPercentage()}%` }}
                                    ></div>
                                </div>
                            </Col>
                        </Row>

                        <Row className="pt-4">
                            <Col>
                                <div className="text-center">
                                    <Spinner animation="border" role="status" className="mb-3">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                    <h4 className="text-white">Processing...</h4>
                                </div>
                            </Col>
                        </Row>                        
                    </Col>                            
                </Row>
            </div>
        );
    }

    return (
        <>
            <div className="pt-5 onboarding-section">    
                <Row className="onboarding-header">
                    <Col xs={1} md={2}></Col>
                    <Col xs={10} md={8}>       
                            
                        <Row className="text-center onboarding-title-row">
                            <Col className="onboarding-title">Tell Us About Your League</Col>
                        </Row>
                        
                        {/* Progress Bar */}
                        <Row className="mt-4">
                            <Col>
                                <div className="text-center mb-1">
                                    <span className="onboarding-progress-percentage">
                                        {Math.round(getProgressPercentage())}% Complete
                                    </span>
                                </div>
                                <div className="onboarding-progress">
                                    <div 
                                        className="onboarding-progress-bar"
                                        style={{ width: `${getProgressPercentage()}%` }}
                                    ></div>
                                </div>
                            </Col>
                        </Row>

                        {/* Examples Section */}
                        {showExamples && (
                            <Row className="mt-4">
                                <Col>
                                    <div className="league-context-examples">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="examples-title mb-0">💡 Example</h6>
                                            <Button 
                                                variant="link" 
                                                size="sm" 
                                                className="text-muted p-0"
                                                onClick={() => setShowExamples(false)}
                                            >
                                                Hide
                                            </Button>
                                        </div>
                                        <div className="examples-content">
                                            <div dangerouslySetInnerHTML={{ 
                                                __html: getExampleText()
                                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                    .replace(/• /g, '• ')
                                                    .replace(/\n/g, '<br/>')
                                            }} />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {!showExamples && (
                            <Row className="mt-3">
                                <Col>
                                    <Button 
                                        variant="link" 
                                        size="sm" 
                                        className="text-muted p-0"
                                        onClick={() => setShowExamples(true)}
                                    >
                                        💡 Show example
                                    </Button>
                                </Col>
                            </Row>
                        )}

                        {/* Formatting Toolbar */}
                        <Row className="mt-4">
                            <Col>
                                <div className="formatting-toolbar">
                                    <div className="toolbar-group">
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => formatText('bold')}
                                            title="Bold selected text"
                                        >
                                            <strong>B</strong>
                                        </Button>
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => formatText('bullet')}
                                            title="Add bullet point"
                                        >
                                            ☰
                                        </Button>
                                    </div>
                                    <div className="toolbar-group emoji-picker-container">
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                            title="Add emoji"
                                        >
                                            😊
                                        </Button>
                                        {showEmojiPicker && (
                                            <div className="emoji-picker-wrapper">
                                                <EmojiPicker
                                                    onEmojiClick={onEmojiClick}
                                                    width={300}
                                                    height={400}
                                                    searchPlaceholder="Search emojis..."
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Text Input */}
                        <Row className="mt-3">
                            <Col>
                                <Form.Group>
                                    <Form.Control
                                        as="textarea"
                                        id="leagueContextTextarea"
                                        rows={8}
                                        value={leagueContext}
                                        onChange={handleTextChange}
                                        placeholder={`Tell us about your league members, rivalries, inside jokes, or anything that makes your league unique and your recap more personal...

[Team 1 Name] is run by John and he's brothers with [Team 2 Name]. [Team 1 Name] talks a lot of crap and he's a software engineer who's always trying to make shady trades.`}
                                        className="league-context-textarea"
                                    />
                                    
                                    {/* Character count - all screen sizes */}
                                    <div className="mt-2 text-end">
                                        <small className={`character-counter ${getRemainingCharacters() < 50 ? 'text-warning' : 'text-muted'}`}>
                                            {getRemainingCharacters()} characters remaining
                                        </small>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className='align-items-center mt-4 justify-content-center'>
                            <Col xs="auto">
                                <Button 
                                    onClick={handleSkip}
                                    disabled={isSubmitting}
                                    variant="outline-secondary"
                                    className="btn-lg mx-2 onboarding-nav-btn"
                                >
                                    Skip
                                </Button>
                            </Col>
                            <Col xs="auto">
                                <Button 
                                    onClick={handleContinue}
                                    disabled={isSubmitting}
                                    className={`btn-lg mx-2 onboarding-nav-btn ${leagueContext.trim() ? 'btn-primary-gradient' : 'btn-secondary'}`}
                                >
                                    {isSubmitting ? 'Saving...' : 'Next →'}
                                </Button>
                            </Col>
                        </Row>                                                 
                    </Col>                            
                </Row>
            </div>
        </>
    );
};

function mapStateToProps({auth}) {
    return { auth };
}

export default connect(mapStateToProps)(LeaguePersonalizationInput);
